import { ChakraStylesConfig } from "chakra-react-select/dist/types/types";
import { SelectOptionData } from "shared/components/input/select/SelectTypes";
import { match } from "ts-pattern";

export const chakraSelectStyles: ChakraStylesConfig<SelectOptionData> = {
  control: (provided, state) => ({
    ...provided,
    backgroundColor: "white",
    w: "100%",
    color: "gray.500",
    flexWrap: "unset",
    p: "",
    pl: "16px",
    pr: "12px",
    cursor: "pointer",
    border: "1px solid",
    borderColor: "gray.300",
    borderRadius: "4px",
    _focusWithin: {
      borderColor: "primary.500",
    },
    "&[data-readonly]": {
      borderColor: "gray.50",
      ".react-select__dropdown-indicator": {
        display: "none",
      },
    },
    ".chakra-form-control[data-invalid] &": {
      borderColor: "error.600",
    },
    ".react-select__search-placeholder": {
      visibility: "hidden",
    },
    "&:has(.react-select__input:focus-visible:not([data-hidden]))": {
      ".react-select__search-placeholder": {
        visibility: "visible",
      },
    },
    ...match(state.selectProps.size)
      .with("sm", () => ({
        py: "2px",
        minHeight: "32px",
        pl: "12px",
        pr: "4px",
      }))
      .otherwise(() => ({
        py: "9px",
        height: "48px",
        minHeight: "36px",
      })),
  }),
  container: (provided, state) => ({
    ...provided,
    padding: "unset",
    paddingRight: "12px",
    paddingLeft: "12px",
    paddingInlineEnd: 0,
    paddingInlineStart: 0,
    whiteSpace: "nowrap",
    ...match(state.selectProps.size)
      .with("sm", () => ({}))
      .otherwise(() => ({
        minHeight: "40px",
      })),
    ".react-select__single-value": {
      ml: "0",
      mr: "0",
    },
    "&:has(.react-select__control--is-focused)": {
      ".react-select__clear-indicator": {
        p: 0,
        display: "flex",
      },
      "&:has(.react-select__value-container--has-value):has(.react-select__clear-indicator) .react-select__dropdown-indicator":
        {
          display: "none",
        },
    },
    "&:has(.react-select__input:focus-visible:not([data-hidden]))": {
      ".react-select__value-container": {
        gridTemplateColumns: "minmax(0, min-content) minmax(min-content, auto)",
        "&:has(.react-select__single-value, .react-select__multi-value)": {
          gridTemplateColumns: "minmax(0, min-content) min-content",
        },
        ".react-select__single-value, .react-select__multi-value": {
          gridArea: "1/1/2/2",
        },
        ".react-select__input-container:first-of-type": {
          gridArea: "1/2/2/4",
        },
      },
    },
  }),
  valueContainer: (provided) => ({
    ...provided,
    px: 0,
    py: 0,
    flexWrap: "nowrap",
    display: "grid",
  }),
  inputContainer: (provided) => ({
    ...provided,
    ml: 0,
    display: "grid",
    mt: "14px",
    gridTemplateColumns: "auto 0",
    gridArea: "1/2/2/3",
  }),
  input: (provided) => ({
    ...provided,
    gridArea: "",
    "&:not([aria-expanded='true'])": {
      cursor: "pointer",
    },
  }),
  indicatorsContainer: (provided) => ({
    ...provided,
    alignSelf: "unset",
  }),
  dropdownIndicator: (_, state) => ({
    p: 0,
    color: state.isDisabled ? "gray.300" : "gray.500",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    ...match(state.selectProps.size)
      .with("sm", () => ({
        w: "24px",
        h: "24px",
        mr: "-2px",
      }))
      .otherwise(() => ({
        w: "38px",
        h: "38px",
        mr: "-8px",
      })),
  }),
  singleValue: (provided) => ({
    ...provided,
    ml: 0,
    mt: "14px",
    mr: "4px",
    gridArea: "1/1/2/3",
    textStyle: "body",
    color: "gray.700",
  }),
  multiValue: (provided) => ({
    ...provided,
    mt: "14px",
    mr: "4px",
    gridArea: "1/1/2/3",
    textStyle: "body",
    color: "gray.700",
  }),
  option: (_, state) => ({
    display: "flex",
    gap: "8px",
    alignItems: "center",
    minHeight: "40px",
    py: "6px",
    px: "12px",
    bg: state.isFocused ? "primary.50" : undefined,
    whiteSpace: "nowrap",
    width: "100%",
    cursor: "pointer",
    _active: {
      bg: "primary.100",
    },
    "&.react-select__option--is-new": {
      color: "primary.700",
      borderBottomWidth: "1px",
      _last: {
        borderBottomColor: "transparent",
      },
      _highlighted: {
        bg: "primary.50",
      },
      _active: {
        bg: "primary.100",
      },
    },
    "&.react-select__option--is-disabled": {
      opacity: 0.5,
      pointerEvents: "none",
    },
  }),
  menu: (provided) => ({
    ...provided,
    my: 0,
    width: "max-content",
    minWidth: "100%",
    maxWidth: "900px",
  }),
  menuList: (provided) => ({
    ...provided,
    border: 0,
    shadow: "md",
    py: 0,
  }),
  clearIndicator: (_, state) => ({
    display: "none",
    ...match(state.selectProps.size)
      .with("sm", () => ({
        w: "24px",
        h: "24px",
        mr: "-2px",
      }))
      .otherwise(() => ({
        mr: "-8px",
      })),
  }),
  noOptionsMessage: () => ({
    p: "16px",
    textAlign: "center",
  }),
  groupHeading: () => ({
    textStyle: "overline",
    px: "12px",
    py: "6px",
  }),
  placeholder: (provided) => ({
    ...provided,
    visibility: "hidden",
    ml: 0,
    mt: "14px",
    color: "gray.400",
    textStyle: "body",
  }),
};
